<template>
  <div>
    <div class="grid-x">
      <div class="small-order-1 cell hide-for-print">
        <MedalliaSurvey page="return-confirmation" />
        <h1>Return Submitted</h1>
      </div>
      <div class="small-order-2 small-12 large-auto large-margin-top-xs">
        <div class="hide-for-print">
          <p v-if="!singleRMA" class="margin-top-xs" v-text="subHeader"></p>
          <p class="margin-top-xs">
            <span v-text="headerText"></span>
            <span class="heavy" v-text="response.returnEmail"></span>
          </p>
          <Notification
            v-if="returnConfirmationStore.returnLabel.showNotification && !!singleRMA"
            class="margin-top-xs"
            severity="success"
            :closable="false"
            aria-live="assertive"
          >
            We emailed your return confirmation to
            {{ returnConfirmationStore.returnLabel.emailAddress }}.
          </Notification>
          <button v-if="!!singleRMA" class="margin-top-xs link-body" type="button" @click="resendEmailHandler">Resend email</button>
          <Notification
            v-for="notification in notifications"
            :key="notification.text"
            :class="notification.class || 'margin-top-s'"
            :severity="notification.type"
            :closable="notification.closable || false"
          >
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-html="notification.text"></div>
          </Notification>
        </div>
        <RMADetail
          v-for="(rma, index) in response.rmaList"
          :key="rma.rmaId"
          :is-single-rma="!!singleRMA"
          :rma-data="rma"
          :packages-count="response.packagesCount"
          :is-boris-return="isBorisReturn"
          :is-first-package="index === 0"
        />
      </div>
      <!-- Refund summary details -->
      <!-- eslint-disable max-len -->
      <div
        v-if="summarySection.length"
        class="hide-for-print small-12 small-order-4 small-border-top small-padding-top-s small-margin-top-s large-4 large-order-3 large-offset-1 large-padding-top-z"
        :class="{ ' small-padding-top-xxs large-margin-top-xs': !singleRMA }"
      >
        <div :class="{ 'border-bottom padding-bottom-xs': !singleRMA }">
          <div
            v-for="(rma, index) in summarySection"
            :key="rma.label"
            class="grid-x align-justify"
            :class="[rma.config?.class, { 'margin-top-xxs': index > 0 }]"
            role="cell"
            :aria-label="rma.value ? rma.label + ' ' + rma.value : rma.label"
          >
            <span :class="rma.config?.labelClass" aria-hidden="true" v-text="rma.label"></span>
            <span :class="rma.config?.valueClass" aria-hidden="true" v-text="rma.value"></span>
          </div>
        </div>
      </div>
      <div
        v-if="showRefundInstructions"
        class="small-order-4 large-order-4 small-margin-top-s large-margin-top-m border-top large-7 small-12 hide-for-print"
      >
        <RefundInstructions :return-details="singleRMA" aria-live="off" show-title :is-boris-return="isBorisReturn" />
      </div>
    </div>
    <ProsWrapper
      class="small-padding-top-s large-padding-top-z small-border-top large-margin-top-m hide-for-print"
      align-horizontal
      data-context="PR_ZONE_A"
      :items="prosItems"
    />
  </div>
</template>

<script setup>
// Imports
import { computed, defineAsyncComponent, markRaw, onMounted } from 'vue';
import Notification from '@atomic-ui/notification/Notification';

// Local Imports
import RMADetail from '@/components/returns/RMADetail.vue';
import MedalliaSurvey from '@/components/MedalliaSurvey.vue';
import ProsWrapper from '@/components/ProsWrapper.vue';

// Config
import { RMA_DETAIL, RMA_SHIPPING_DETAILS_ERROR, REFUND_METHOD, REFUND_METHOD_TEXT, RETURN_METHOD_KEY_MAP, brand } from 'config';

// Utility Functions and Constant
import { formatPrice } from '@/utils/returns/General';
import { FREE_SHIPMENT, CONDITIONAL_RETURN_FEE_ELIGIBLE_RETURN_METHODS } from '#shared/constants';

// Stores and Composable
import { useReturnConfirmationStore } from '@/stores/returns/useReturnConfirmationStore';
import { useReturnItemStore } from '@/stores/returns/useReturnItemStore';

const { registerModal, toggleModal } = useModal();
const returnConfirmationStore = useReturnConfirmationStore();
const returnItemsStore = useReturnItemStore();

const response = computed(() => {
  const order = returnConfirmationStore.returnDetails?.returnResponse?.[0];
  if (!order) return { rmaList: [], packagesCount: {}, rmaTypes: [] };

  const packagesCount = {};
  const rmaTypes = [];

  return {
    ...order,
    rmaList: (order.rmaList || []).map((rma) => {
      if (!rmaTypes.includes(rma.rmaType)) rmaTypes.push(rma.rmaType);
      const packageKey = `${rma.rmaType}_${rma.returnDropOffMethod}`;
      packagesCount[packageKey] = (packagesCount[packageKey] || 0) + 1;
      const packageSequence = packagesCount[packageKey];

      return {
        ...rma,
        orderNumber: order.orderNumber,
        reservationNumber: order.reservationNumber,
        returnEmail: order.returnEmail,
        fineJewelryProcessingDuration: order.fineJewelryProcessingDuration,
        inTransitDuration: order.inTransitDuration,
        giftCardDuration: order.giftCardDuration,
        refundWindow: order.refundWindow,
        conditionalReturnFeeEnabled: order.conditionalReturnFeeEnabled,
        eWasteFeeEnabled: order.eWasteFeeEnabled,
        isMkpOrder: order.isMkpOrder,
        packageSequence,
        packageKey,
        isNewGoogleAPICallEnabled: order.isNewGoogleAPICallEnabled,
      };
    }),
    packagesCount,
    rmaTypes,
  };
});
const prosItems = computed(() =>
  response.value.rmaList.flatMap((rma) =>
    rma.rmaLines.map((line) => ({
      productId: line.productId,
    })),
  ),
);
const singleRMA = computed(() => {
  if (response.value.rmaList.length === 1) return response.value.rmaList[0];
  return false;
});

const resendEmailRmaId = computed(() => {
  const rmaList = response.value?.rmaList || [];
  return rmaList[0]?.rmaId || false;
});

const subHeader = computed(() => {
  const shippedReturnPackageCount = response.value.packagesCount[`${RMA_DETAIL.RETURN_REQUIRED.TYPE}_${RETURN_METHOD_KEY_MAP.ups}`];
  if (shippedReturnPackageCount > 1 && !response.value.rmaTypes.includes(RMA_DETAIL.REFUND_RETURN_NOT_REQUIRED.TYPE)) {
    return `Your return should be sent back in ${shippedReturnPackageCount} separate packages`;
  }
  return 'Your return includes items to ship back and items you can keep.';
});

const isBorisReturn = computed(() => {
  const isStoreReturn = response.value.rmaList?.[0]?.returnDropOffMethod === RETURN_METHOD_KEY_MAP.storeReturn;
  return isStoreReturn && response.value.isMkpOrder;
});

async function resendEmailHandler() {
  registerModal({
    id: 'return-label',
    title: 'Where should we send your return confirmation?',
    width: '628px',
    component: markRaw(defineAsyncComponent(() => import('@/components/returns/GetReturnLabel'))),
    props: {
      isReturnConfirmation: true,
      rmaId: resendEmailRmaId.value,
    },
  });
  toggleModal('return-label');
}

const headerText = computed(() => {
  if (!response.value.rmaList?.length) return '';
  const isUPSReturn = response.value.rmaList[0]?.returnDropOffMethod === RETURN_METHOD_KEY_MAP.ups;
  const isFedexReturn = response.value.rmaList[0]?.returnDropOffMethod === RETURN_METHOD_KEY_MAP.fedex;
  if (isBorisReturn.value) {
    return 'We emailed your return confirmation with your return details to ';
  }
  if (isUPSReturn || isFedexReturn || (singleRMA.value && singleRMA.value.rmaType === RMA_DETAIL.REFUND_RETURN_NOT_REQUIRED.TYPE)) {
    return 'We emailed your return confirmation to ';
  }
  return 'We emailed your return confirmation and QR code to ';
});

const refundToValue = (rma) => {
  const { refundMethod, refundToAccountType = '', refundToAccountNumber = '' } = rma;
  const originalPayment = refundMethod === REFUND_METHOD.ORIGINAL && refundToAccountType && refundToAccountNumber;

  let refundToAccount = `${refundToAccountType} ${refundToAccountNumber}`;
  if (refundToAccountType?.toUpperCase() === 'KLARNA') {
    refundToAccount = refundToAccountType;
  }

  if (originalPayment) return refundToAccount;
  if ([REFUND_METHOD.GIFT, REFUND_METHOD.EGIFT, REFUND_METHOD.WALLET].includes(refundMethod)) {
    return REFUND_METHOD_TEXT[refundMethod];
  }
  return REFUND_METHOD_TEXT.ORIGINAL;
};

const createSummaryItems = (rma) => {
  const packageCount = response.value.packagesCount[rma.packageKey];
  const summaryTitle =
    rma.returnDropOffMethod === RETURN_METHOD_KEY_MAP.storeReturn ? RMA_DETAIL.RETURN_ITEM.TITLE : RMA_DETAIL[rma.rmaType].SUMMARY_TITLE;
  return [
    {
      label: summaryTitle,
      condition: !singleRMA.value && rma.packageSequence === 1,
      config: {
        class: 'margin-top-xs',
        labelClass: 'heavy',
      },
    },
    {
      label: `Package ${rma.packageSequence} of ${packageCount}`,
      condition: packageCount > 1,
      config: {
        class: 'margin-top-xs',
        labelClass: 'heavy',
      },
    },
    {
      label: rma.refundMethod === REFUND_METHOD.WALLET ? 'Refund as' : 'Refund to',
      value: refundToValue(rma),
    },
    {
      label: 'Subtotal',
      value: formatPrice(rma.merchandiseTotal),
    },
    {
      label: 'Allstate Protection Plan',
      value: formatPrice(rma.protectionPlanTotal),
      condition: !!rma.protectionPlanTotal,
    },
    {
      label: 'Estimated Tax',
      value: formatPrice(rma.totalTaxes),
      condition: !!rma.totalTaxes,
    },
    {
      label: 'eWaste Recycling Fee',
      value: formatPrice(rma.eWasteFee),
      condition: !!(brand === 'mcom' && response.value.isMkpOrder && rma.eWasteFee),
    },
    {
      label: 'Estimated Return Shipping Fee',
      value: rma.shippingFee === 0.0 ? FREE_SHIPMENT : `-${formatPrice(rma.shippingFee)}`,
      condition: !!(
        brand === 'mcom' &&
        !response.value.isMkpOrder &&
        rma.conditionalReturnFeeEnabled &&
        CONDITIONAL_RETURN_FEE_ELIGIBLE_RETURN_METHODS.includes(rma.returnDropOffMethod)
      ),
    },
    {
      label: 'CA embedded battery fee',
      value: formatPrice(rma.batteryFee),
      condition: !!rma.batteryFee,
    },
    {
      label: 'Estimated refund',
      value: formatPrice(rma.estimatedRefund),
      config: {
        class: {
          'border-top padding-top-xs margin-top-xs': singleRMA.value,
        },
        labelClass: singleRMA.value && 'h4',
        valueClass: singleRMA.value ? 'h4' : 'heavy',
      },
    },
  ];
};

const summarySection = computed(() => response.value.rmaList.flatMap((rma) => createSummaryItems(rma).filter((item) => item.condition !== false)));

const showRefundInstructions = computed(
  () =>
    singleRMA.value &&
    singleRMA.value.rmaType === RMA_DETAIL.RETURN_REQUIRED.TYPE &&
    !(singleRMA.value.returnDropOffMethod === RETURN_METHOD_KEY_MAP.storeReturn && !singleRMA.value.isMkpOrder),
);

const RefundInstructions = computed(() =>
  showRefundInstructions.value
    ? markRaw(defineAsyncComponent(() => import(/* webpackChunkName: "refund-instructions-slideout" */ '@/components/returns/RefundInfo.vue')))
    : null,
);

const notifications = computed(() => {
  const arr = [];
  const web = response.value.orderReturnAlertMessage?.web || {};
  if (web.message) {
    arr.push({
      type: web.message_type || 'info',
      text: web.message,
    });
  }

  if (returnItemsStore.errors?.getShippingDetails) {
    arr.unshift({
      type: 'error',
      text: RMA_SHIPPING_DETAILS_ERROR,
    });
  }

  return arr;
});

onMounted(() => {
  setTimeout(() => {
    medalliaSurveyInit();
  }, 300);
});

function medalliaSurveyInit() {
  const MEDALLIA_SURVEY = window.KAMPYLE_ONSITE_SDK;
  if (!MEDALLIA_SURVEY || typeof MEDALLIA_SURVEY.updatePageView !== 'function') {
    return;
  }
  global.ReturnResolutionOption = response.value.rmaList[0]?.returnResolution;
  global.pathwayForMedallia = response.value.rmaList[0]?.returnDropOffMethod;
  global.marketPlaceItem = response.value.isMkpOrder?.toString();
  global.beneficiaryUserState = response.value.beneficiaryUserState;
  MEDALLIA_SURVEY.updatePageView();
}
</script>

<style lang="scss">
.cta {
  @include breakpoint(medium) {
    max-width: rem-calc(342);
  }
  .summary-container {
    display: flex;
    align-items: flex-end;
  }
}
</style>
